import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { knowledgeAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  Search,
  BookOpen,
  FileText,
  Eye,
  Plus,
  Loader2,
  Home,
  AlertTriangle,
  Edit2,
  Trash2,
  EyeOff,
} from 'lucide-react';
import NewArticleDialog from '@/components/NewArticleDialog';

export default function KnowledgeBase({ embedded = false } = {}) {
  const { articleId } = useParams();
  const { user, isAgent } = useAuth();

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewArticle, setShowNewArticle] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);

  useEffect(() => {
    loadData();
  }, [isAgent]);

  useEffect(() => {
    if (articleId) {
      loadArticle(articleId);
    } else {
      setSelectedArticle(null);
    }
  }, [articleId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [articlesRes, categoriesRes] = await Promise.all([
        knowledgeAPI.list({ published_only: !isAgent }),
        knowledgeAPI.getCategories(),
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Failed to load knowledge base:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadArticle = async (id) => {
    try {
      const res = await knowledgeAPI.get(id);
      setSelectedArticle(res.data);
    } catch (err) {
      console.error('Failed to load article:', err);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete article "${title}"? This cannot be undone.`)) return;
    try {
      await knowledgeAPI.delete(id);
      if (selectedArticle?.id === id) window.history.back();
      await loadData();
    } catch (err) {
      alert('Failed to delete article');
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setShowNewArticle(true);
  };

  const filteredArticles = articles.filter((a) => {
    const matchesSearch =
      searchQuery === '' ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || a.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedArticle) {
    return (
      <div className={embedded ? 'flex-1 overflow-y-auto bg-slate-50' : 'min-h-screen bg-slate-50'} data-testid="article-view">
        {!embedded && (
          <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
              <Link to={embedded ? '/dashboard/knowledge' : '/knowledge'} className="flex items-center gap-1.5 text-slate-600 hover:text-slate-800 transition-colors text-sm sm:text-base">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Back to Knowledge Base</span>
                <span className="sm:hidden">Back</span>
              </Link>
              <div className="flex items-center gap-2">
                {isAgent && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full border-slate-300" onClick={() => handleEdit(selectedArticle)} data-testid="edit-article-btn" aria-label="Edit article">
                      <Edit2 className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleDelete(selectedArticle.id, selectedArticle.title)} data-testid="delete-article-btn" aria-label="Delete article">
                      <Trash2 className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Delete</span>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </header>
        )}
        {embedded && (
          <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => { setSelectedArticle(null); }}
              className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-800 transition-colors text-sm"
              data-testid="kb-back-embedded"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Knowledge Base
            </button>
            <div className="flex items-center gap-2">
              {isAgent && (
                <>
                  <Button size="sm" variant="outline" className="rounded-full border-slate-300" onClick={() => handleEdit(selectedArticle)} data-testid="edit-article-btn">
                    <Edit2 className="w-4 h-4 mr-1.5" />Edit
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleDelete(selectedArticle.id, selectedArticle.title)} data-testid="delete-article-btn">
                    <Trash2 className="w-4 h-4 mr-1.5" />Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <article className="prose prose-slate max-w-none">
            {selectedArticle.warning && (
              <div className="mb-4 sm:mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="article-warning-banner">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 text-sm sm:text-base">Warning</p>
                  <p className="text-sm text-amber-800 whitespace-pre-wrap leading-relaxed">{selectedArticle.warning}</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className="border-cyan-500 text-cyan-700">{selectedArticle.category}</Badge>
              {!selectedArticle.is_published && (
                <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[11px]">
                  <EyeOff className="w-3 h-3 mr-1" />Draft
                </Badge>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-slate-900 leading-tight">{selectedArticle.title}</h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-500 mb-6 sm:mb-8">
              <span>By {selectedArticle.author_name}</span>
              <span className="hidden sm:inline">•</span>
              <span>{formatDate(selectedArticle.created_at)}</span>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{selectedArticle.views} views</span>
            </div>
            <div className="p-4 sm:p-6 bg-white rounded-xl border border-slate-200 whitespace-pre-wrap text-slate-700 leading-relaxed text-sm sm:text-base">
              {selectedArticle.content}
            </div>
            {selectedArticle.tags?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-6">
                <span className="text-sm text-slate-500">Tags:</span>
                {selectedArticle.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="bg-cyan-50 text-cyan-700">{tag}</Badge>
                ))}
              </div>
            )}
          </article>
        </main>

        {isAgent && (
          <NewArticleDialog
            open={showNewArticle}
            onOpenChange={(o) => { setShowNewArticle(o); if (!o) setEditingArticle(null); }}
            article={editingArticle}
            onCreated={() => {
              setEditingArticle(null);
              loadData();
              if (selectedArticle?.id) loadArticle(selectedArticle.id);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={embedded ? 'flex-1 overflow-y-auto bg-slate-50' : 'min-h-screen bg-slate-50'} data-testid="knowledge-base">
      {!embedded && (
        <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shrink-0">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-slate-800 text-sm sm:text-base truncate">Knowledge Base</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              {user ? (
                <Link to={user.role === 'customer' ? '/portal' : '/dashboard'}>
                  <Button variant="outline" className="rounded-full border-slate-300 hover:bg-slate-50 h-9 sm:h-10 px-3 sm:px-4">
                    <Home className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Button>
                </Link>
              ) : (
                <Link to="/auth">
                  <Button className="rounded-full bg-cyan-500 hover:bg-cyan-600 h-9 sm:h-10 px-4">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-slate-800">Knowledge Base</h1>
          <p className="text-sm sm:text-base text-slate-600 px-2">Find answers to common questions and learn how to use our platform</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            <Input placeholder="Search articles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 sm:pl-12 h-11 sm:h-12 bg-white border-slate-200" data-testid="kb-search-input" aria-label="Search articles" />
          </div>
          {isAgent && (
            <Button className="rounded-full bg-cyan-500 hover:bg-cyan-600 h-11" onClick={() => { setEditingArticle(null); setShowNewArticle(true); }} data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" />New Article
            </Button>
          )}
        </div>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6 sm:mb-8 overflow-x-auto pb-1">
            <Button variant={selectedCategory === 'all' ? 'default' : 'outline'} size="sm" className={`shrink-0 rounded-full ${selectedCategory === 'all' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-300 hover:bg-slate-50'}`} onClick={() => setSelectedCategory('all')}>All</Button>
            {categories.map((cat) => (
              <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} size="sm" className={`shrink-0 rounded-full ${selectedCategory === cat ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-300 hover:bg-slate-50'}`} onClick={() => setSelectedCategory(cat)}>{cat}</Button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : filteredArticles.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <h3 className="text-lg font-medium mb-2 text-slate-800">No articles found</h3>
              <p className="text-slate-500">{searchQuery ? 'Try a different search term' : 'No articles have been published yet'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filteredArticles.map((article) => (
              <Card key={article.id} className="bg-white border-slate-200 hover:border-cyan-500 hover:shadow-lg transition-all duration-300 group relative" data-testid={`article-card-${article.id}`}>
                <Link to={`/knowledge/${article.id}`} className="block">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <Badge variant="outline" className="text-xs border-cyan-500 text-cyan-700">{article.category}</Badge>
                      {article.warning && (
                        <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />Warning
                        </Badge>
                      )}
                      {!article.is_published && (
                        <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">Draft</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold mb-2 text-slate-800 group-hover:text-cyan-600 transition-colors leading-snug">{article.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4">{article.content.substring(0, 150)}...</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{formatDate(article.created_at).split(',')[0]}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.views}</span>
                    </div>
                  </CardContent>
                </Link>
                {isAgent && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <Button size="icon" variant="ghost" className="h-8 w-8 bg-white/95 border border-slate-200 hover:bg-white text-slate-600" onClick={(e) => { e.preventDefault(); handleEdit(article); }} data-testid={`edit-card-${article.id}`} aria-label={`Edit ${article.title}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 bg-white/95 border border-slate-200 hover:bg-red-50 text-red-600" onClick={(e) => { e.preventDefault(); handleDelete(article.id, article.title); }} data-testid={`delete-card-${article.id}`} aria-label={`Delete ${article.title}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      {isAgent && (
        <NewArticleDialog
          open={showNewArticle}
          onOpenChange={(o) => { setShowNewArticle(o); if (!o) setEditingArticle(null); }}
          article={editingArticle}
          onCreated={() => { setEditingArticle(null); loadData(); }}
        />
      )}
    </div>
  );
}
